import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { customFieldService, CustomProductField } from '../../services/configurationService';
import { customToast } from '../../utils/customToast';
import { X, Plus, Trash2, Settings } from 'lucide-react';

interface CustomFieldModalProps {
  field: CustomProductField | null;
  onClose: () => void;
  onSave: () => void;
}

const CustomFieldModal: React.FC<CustomFieldModalProps> = ({ field, onClose, onSave }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    field_name: '',
    field_type: 'text' as 'text' | 'number' | 'date' | 'textarea' | 'select' | 'checkbox',
    description: '',
    options: [] as string[],
    default_value: '',
    is_required: false,
    is_active: true,
    validation_rules: '',
    sort_order: 0
  });
  const [newOption, setNewOption] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (field) {
      setFormData({
        field_name: field.field_name,
        field_type: field.field_type as any,
        description: field.description || '',
        options: field.options || [],
        default_value: field.default_value || '',
        is_required: field.is_required,
        is_active: field.is_active,
        validation_rules: field.validation_rules || '',
        sort_order: field.sort_order
      });
    }
  }, [field]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.field_name.trim()) {
      customToast.error('Please enter a field name');
      return;
    }

    if (formData.field_type === 'select' && formData.options.length === 0) {
      customToast.error('Please add at least one option for dropdown field');
      return;
    }

    try {
      setSaving(true);
      const dataToSend = {
        ...formData,
        options: formData.field_type === 'select' ? formData.options : undefined,
      };

      if (field) {
        await customFieldService.update(field.id, dataToSend);
        customToast.success('Field updated successfully');
      } else {
        await customFieldService.create(dataToSend);
        customToast.success('Field created successfully');
      }
      onSave();
    } catch (error: any) {
      const errorMsg = error.response?.data?.errors?.field_name?.[0] || 
                      error.response?.data?.message ||
                      'Failed to save field';
      customToast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setFormData({
        ...formData,
        options: [...formData.options, newOption.trim()]
      });
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    });
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] animate-fadeIn`}
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl animate-scaleIn ${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <Settings className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  {field ? 'Edit Custom Field' : 'Add New Custom Field'}
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {field ? 'Update custom field settings' : 'Create additional product field'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}>
        <form onSubmit={handleSubmit} id="custom-field-form" className="p-4 sm:p-5 md:p-6">
          <div className="space-y-4 sm:space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Field Name *
                </label>
                <input
                  type="text"
                  value={formData.field_name}
                  onChange={(e) => setFormData({ ...formData, field_name: e.target.value })}
                  className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-0 transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                  }`}
                  placeholder="e.g., Expiry Date"
                  required
                />
              </div>

              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Field Type *
                </label>
                <select
                  value={formData.field_type}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    field_type: e.target.value as any,
                    options: e.target.value === 'select' ? formData.options : []
                  })}
                  className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-0 transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white'
                      : 'bg-white border-slate-300 text-slate-900'
                  }`}
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="textarea">Text Area</option>
                  <option value="select">Dropdown</option>
                  <option value="checkbox">Checkbox</option>
                </select>
              </div>
            </div>

            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-0 transition-colors touch-manipulation resize-none ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                }`}
                placeholder="Enter description (optional)"
              />
            </div>

            {/* Options for Select Field */}
            {formData.field_type === 'select' && (
              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Dropdown Options *
                </label>
                
                {/* Add Option */}
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                    className={`flex-1 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-0 transition-colors touch-manipulation ${
                      theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                        : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                    }`}
                    placeholder="Enter option and press Add"
                  />
                  <button
                    type="button"
                    onClick={handleAddOption}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5 touch-manipulation"
                  >
                    <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Add</span>
                  </button>
                </div>

                {/* Options List */}
                {formData.options.length > 0 && (
                  <div className={`border rounded-lg p-2 space-y-1.5 ${
                    theme === 'dark' ? 'border-slate-600' : 'border-slate-300'
                  }`}>
                    {formData.options.map((option, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg ${
                          theme === 'dark' ? 'bg-slate-700' : 'bg-slate-50'
                        }`}
                      >
                        <span className={`text-xs sm:text-sm flex-1 truncate ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
                          {option}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(index)}
                          className="text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 p-1 rounded touch-manipulation flex-shrink-0 ml-2"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Default Value
              </label>
              <input
                type="text"
                value={formData.default_value}
                onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-0 transition-colors touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                }`}
                placeholder="Enter default value (optional)"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={formData.is_required}
                  onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded touch-manipulation"
                />
                <label htmlFor="is_required" className={`ml-2 text-xs sm:text-sm select-none ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Required Field
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded touch-manipulation"
                />
                <label htmlFor="is_active" className={`ml-2 text-xs sm:text-sm select-none ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Active
                </label>
              </div>
            </div>
          </div>
        </form>
        </div>

          {/* Footer */}
          <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${
            theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
          }`}>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${
                  theme === 'dark'
                    ? 'border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-slate-500'
                    : 'border-slate-300 text-slate-700 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="custom-field-form"
                disabled={saving}
                className="w-full sm:flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 touch-manipulation bg-blue-500 hover:bg-blue-600"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
      </div>
    </div>,
    document.body
  );
};

export default CustomFieldModal;
